import { TriangleAlert } from "lucide-react";
import { useParams } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useFinancialSituationModels } from "@/lib/hooks/use-financial-situation-models";
import {
  useGoalTypes,
  usePlatforms,
  usePortfolioCollection,
  usePortfolioCollectionSettings,
  useProducts,
} from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";

import { TableCardSkeleton } from "../skeleton-loaders";
import { T } from "./page.i18n";
import { PlatformsTable } from "./platforms-table";

export default function PlatformsPage() {
  const { id } = useParams<{ id: string }>();
  const { platforms, isLoading } = usePlatforms(id!);
  const { goalTypes, isLoading: goalTypesLoading } = useGoalTypes(id!);
  // The FS-model relation (Settings tab) gates the platform-conditions editor:
  // conditions are pickers over that model's derived statuses.
  const { settings, isLoading: settingsLoading } = usePortfolioCollectionSettings(id!);
  const { models, isLoading: modelsLoading } = useFinancialSituationModels();
  // Products feed the platform dialog's membership picker (QP-226); they are
  // not needed for first paint, so their fetch doesn't gate the skeleton.
  const { products, isLoading: productsLoading } = useProducts(id!);
  // QL-198 — the detail payload embeds platformStatus (Platforms tab warning).
  const { collection } = usePortfolioCollection(id!);
  const t = useT(T);

  if (isLoading || goalTypesLoading || settingsLoading || modelsLoading) {
    return <TableCardSkeleton columns={5} />;
  }

  const fsModelId = settings?.financialSituationModelId ?? null;
  const statusDefs = fsModelId ? (models.find((m) => m.id === fsModelId)?.statuses ?? []) : null;

  // QL-198 — platforms with no active model portfolio tagged (QP-226 — no
  // untagged fallback, so goals on them can never resolve model advice).
  const platformIdsMissingPortfolios =
    collection?.platformStatus?.platformIdsWithoutModelPortfolios ?? [];

  return (
    <div className="space-y-4">
      {platformIdsMissingPortfolios.length > 0 && (
        <Alert variant="warning">
          <AlertDescription className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
            {t.platformsMissingPortfoliosWarning}
          </AlertDescription>
        </Alert>
      )}
      <PlatformsTable
        portfolioCollectionId={id!}
        platforms={platforms}
        goalTypes={goalTypes}
        statusDefs={statusDefs}
        products={products}
        productsLoading={productsLoading}
        platformIdsMissingPortfolios={platformIdsMissingPortfolios}
      />
    </div>
  );
}
